import { inject, Injectable } from "@angular/core";
import { Action, NgxsOnInit, Selector, State, StateContext } from "@ngxs/store";
import { CookieConsent } from "src/app/_core/state/dialogs/cookies/service/models/cookie-consent";
import { AcceptAllCookies, ChangeCookiesStep, CloseCookiesDialog, ConfirmPreferences, OpenCookiesDialog, RejectCookies, SetCookieConsent } from "./cookies.actions";
import { CookiesService } from "./service/cookies.service";
import { patch, updateItem } from "@ngxs/store/operators";
import { CookieStep } from "./service/models/cookie-step.model";
import { SupportService } from "src/app/services/support.service";
import { GoogleTagManagerService } from "src/app/services/google-tag-manager.service";
import { HeapService } from "src/app/services/heap.service";
import { AnalyticsService } from "src/app/services/analytics.service";

export type CookiesStateModel = {
  isOpen: boolean,
  step: CookieStep,
  cookies: CookieConsent[],
  hasConsent: boolean,
};

export const initialState: CookiesStateModel = {
  isOpen: false,
  step: 'overview',
  cookies: [],
  hasConsent: false,
};

@State<CookiesStateModel>({
  name: 'cookies',
  defaults: initialState,
})
@Injectable()
export class CookiesState implements NgxsOnInit {
  private readonly cookiesService = inject(CookiesService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly heapService = inject(HeapService);
  private readonly googleTagManagerService = inject(GoogleTagManagerService);
  private readonly supportService = inject(SupportService);

  @Selector()
  static isOpen(state: CookiesStateModel): boolean {
    return state.isOpen;
  }

  @Selector()
  static requiredCookies(state: CookiesStateModel): CookieConsent[] {
    return state.cookies.filter(cookie => cookie.required);
  }

  @Selector()
  static optionalCookies(state: CookiesStateModel): CookieConsent[] {
    return state.cookies.filter(cookie => !cookie.required && cookie.show);
  }

  @Selector()
  static step(state: CookiesStateModel): CookieStep {
    return state.step;
  }

  @Selector()
  static hasConsent(state: CookiesStateModel): boolean {
    return state.hasConsent;
  }

  ngxsOnInit(ctx: StateContext<CookiesStateModel>): void {
    ctx.patchState({
      hasConsent: this.cookiesService.hasCookies(),
    });
  }

  @Action(OpenCookiesDialog)
  openCookiesDialog(context: StateContext<CookiesStateModel>) {
    const cookies = this.cookiesService.getCookies();

    context.patchState({
      isOpen: true,
      cookies,
      step: 'overview',
    });
  }

  @Action(CloseCookiesDialog)
  closeCookiesDialog(context: StateContext<CookiesStateModel>) {
    context.patchState({
      isOpen: false,
    });
  }

  @Action(SetCookieConsent)
  setCookieConsent(context: StateContext<CookiesStateModel>, { cookieId, accepted }: SetCookieConsent) {
    context.setState(
      patch({
        cookies: updateItem<CookieConsent>((cookie) => cookie.id === cookieId, patch({ accepted })),
      })
    );
  }

  @Action(AcceptAllCookies)
  acceptAllCookies(context: StateContext<CookiesStateModel>) {
    const state = context.getState();

    const optionalCookies = this.getOptionalCookies(state).map(cookie => ({ ...cookie, accepted: true }))
    const allCookies = [...this.getRequiredCookies(state), ...optionalCookies];

    this.cookiesService.setCookiesLocalStorage(allCookies);
    this.initAnalytics();

    context.patchState({
      isOpen: false,
      cookies: [
        ...this.getRequiredCookies(state),
        ...optionalCookies,
      ],
      hasConsent: true,
    });
  }

  @Action(RejectCookies)
  rejectCookies(context: StateContext<CookiesStateModel>) {
    const state = context.getState();

    const optionalCookies = this.getOptionalCookies(state).map(cookie => ({ ...cookie, accepted: false }))
    const allCookies = [...this.getRequiredCookies(state), ...optionalCookies];

    this.cookiesService.setCookiesLocalStorage(allCookies);
    this.initAnalytics();

    context.patchState({
      isOpen: false,
      cookies: [
        ...this.getRequiredCookies(state),
        ...optionalCookies,
      ],
      hasConsent: true,
    });
  }

  @Action(ConfirmPreferences)
  confirmPreferences(context: StateContext<CookiesStateModel>) {
    const state = context.getState();

    this.cookiesService.setCookiesLocalStorage([...state.cookies]);
    this.initAnalytics();

    context.patchState({
      isOpen: false,
      hasConsent: true,
    });
  }

  @Action(ChangeCookiesStep)
  changeCookiesStep(context: StateContext<CookiesStateModel>, { step }: ChangeCookiesStep) {
    context.patchState({
      step,
    });
  }

  private getOptionalCookies(state: CookiesStateModel): CookieConsent[] {
    return state.cookies.filter(cookie => !cookie.required && cookie.show);
  }

  private getRequiredCookies(state: CookiesStateModel): CookieConsent[] {
    return state.cookies.filter(cookie => cookie.required);
  }

  private initAnalytics() {
    this.analyticsService.init();
    this.googleTagManagerService.init();
    this.heapService.init();
    this.supportService.init();
  }
}